import React, { useEffect, useMemo, useState } from "react";
import { FormControlLabel, Switch } from "@mui/material";
import FormDialog from "../../../../components/common/FormDialog";
import FormField from "../../../../components/common/FormField";
import ChipAutocomplete from "../../../../components/common/ChipAutocomplete";
import useFormValidation from "../../../../hooks/useFormValidation";
import { required } from "../../../../utils/formValidation";
import { DEFAULT_GRANT_TYPES, GRANT_TYPE_OPTIONS } from "../../constants";

const schema = {
  name: [required("Client name is required.")],
};

const ClientFormDialog = ({
  open,
  roles = [],
  rolesLoading = false,
  loading = false,
  showExtendedFields = false,
  onClose,
  onSubmit,
}) => {
  const [formError, setFormError] = useState("");
  const [grantType, setGrantType] = useState(DEFAULT_GRANT_TYPES);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [redirectUri, setRedirectUri] = useState([]);
  const [allowUserCreation, setAllowUserCreation] = useState(!showExtendedFields);
  const [allowSelfUserCreation, setAllowSelfUserCreation] = useState(false);
  const [allowLoginViaOTP, setAllowLoginViaOTP] = useState(false);

  const { values, getFieldProps, validateAll, reset } = useFormValidation(
    { name: "" },
    schema
  );

  useEffect(() => {
    if (!open) return;
    setFormError("");
    reset({ name: "" });
    setGrantType(DEFAULT_GRANT_TYPES);
    setSelectedRoles([]);
    setRedirectUri([]);
    setAllowUserCreation(!showExtendedFields);
    setAllowSelfUserCreation(false);
    setAllowLoginViaOTP(false);
  }, [open, reset, showExtendedFields]);

  const roleOptions = useMemo(() => {
    const fromApi = (roles || []).map((role) =>
      typeof role === "string" ? role : role?.name || role?.role || String(role)
    );
    return Array.from(new Set([...fromApi, ...selectedRoles].filter(Boolean)));
  }, [roles, selectedRoles]);

  const handleSubmit = async () => {
    const { isValid, errors } = validateAll();
    if (!isValid) {
      setFormError(errors.name || "Please correct the highlighted fields.");
      return;
    }
    if (!grantType.length) {
      setFormError("Select at least one grant type.");
      return;
    }
    if (!selectedRoles.length) {
      setFormError("Select at least one role.");
      return;
    }

    setFormError("");
    try {
      await onSubmit({
        name: values.name.trim(),
        grantType,
        allowUserCreation,
        allowSelfUserCreation,
        allowLoginViaOTP,
        redirectUri,
        roles: selectedRoles,
      });
    } catch (err) {
      setFormError(err?.message || "Unable to create client.");
    }
  };

  return (
    <FormDialog
      open={open}
      title="Add Client"
      onClose={onClose}
      onSubmit={handleSubmit}
      loading={loading}
      error={formError}
      onClearError={() => setFormError("")}
      submitLabel="Create Client"
      loadingLabel="Creating..."
    >
      <FormField label="Client Name" required {...getFieldProps("name")} />
      <ChipAutocomplete
        label="Grant Type"
        required
        options={GRANT_TYPE_OPTIONS}
        value={grantType}
        onChange={setGrantType}
        disabled={loading}
      />
      <ChipAutocomplete
        label="Roles"
        required
        freeSolo
        options={roleOptions}
        value={selectedRoles}
        onChange={setSelectedRoles}
        loading={rolesLoading}
        disabled={loading}
      />
      {showExtendedFields ? (
        <ChipAutocomplete
          label="Redirect URIs"
          freeSolo
          options={redirectUri}
          value={redirectUri}
          onChange={setRedirectUri}
          disabled={loading}
          placeholder="https://example.com/callback"
        />
      ) : null}
      <FormControlLabel
        sx={{ ml: 0, alignItems: "center" }}
        control={
          <Switch
            checked={allowUserCreation}
            onChange={(event) => setAllowUserCreation(event.target.checked)}
            disabled={loading}
          />
        }
        label="Allow user creation"
      />
      {showExtendedFields ? (
        <>
          <FormControlLabel
            sx={{ ml: 0, alignItems: "center" }}
            control={
              <Switch
                checked={allowSelfUserCreation}
                onChange={(event) => setAllowSelfUserCreation(event.target.checked)}
                disabled={loading}
              />
            }
            label="Allow self user creation"
          />
          <FormControlLabel
            sx={{ ml: 0, alignItems: "center" }}
            control={
              <Switch
                checked={allowLoginViaOTP}
                onChange={(event) => setAllowLoginViaOTP(event.target.checked)}
                disabled={loading}
              />
            }
            label="Allow login via OTP"
          />
        </>
      ) : null}
    </FormDialog>
  );
};

export default ClientFormDialog;
